/**
 * Stock, per product, with the evidence one tap away.
 *
 * On a phone this is a list of cards rather than a table: a seven-column table
 * on a 380px screen is either unreadable or horizontally scrolled, and an
 * owner will not scroll sideways to find out whether they have been robbed.
 * The same data becomes a real table from `sm` upward, where there is room.
 *
 * The drill-in exists because the headline number is the part nobody believes.
 * "Expected 18" invites "says who?", and the answer has to be the movements
 * that produced it, in order, with names.
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, History } from 'lucide-react';
import { api } from '../../lib/api';
import { Badge, EmptyState, Spinner } from '../../components/ui';
import { money, moment, quantity } from './format';
import type { ProductEvent, StockLine } from './types';

export function StockTable({
  lines,
  currency,
  timezone,
  businessId,
}: {
  lines: StockLine[];
  currency: string;
  timezone: string;
  businessId: string;
}): ReactNode {
  const [openProduct, setOpenProduct] = useState<string | null>(null);

  if (lines.length === 0) {
    return (
      <EmptyState
        title="No stock moved in this period"
        description="Nothing was received, sold or adjusted between these dates."
      />
    );
  }

  return (
    <div className="space-y-2">
      {/* Cards on a phone. */}
      <div className="space-y-2 sm:hidden">
        {lines.map((line) => (
          <div key={line.productId} className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium text-slate-900">{line.productName}</p>
              <p className="shrink-0 text-sm font-semibold tabular-nums">
                {quantity(line.onHand)} left
              </p>
            </div>

            <dl className="mt-2 grid grid-cols-3 gap-2 text-xs text-slate-600">
              <div>
                <dt className="opacity-70">In</dt>
                <dd className="tabular-nums">{quantity(line.received)}</dd>
              </div>
              <div>
                <dt className="opacity-70">Sold</dt>
                <dd className="tabular-nums">{quantity(line.sold)}</dd>
              </div>
              <div>
                <dt className="opacity-70">Removed</dt>
                <dd className="tabular-nums">{quantity(line.wasted + Math.abs(line.adjusted))}</dd>
              </div>
            </dl>

            <p className="mt-2 text-xs text-slate-500">
              Worth {money(line.remainingValue, currency)}
              {line.removedBy.length > 0 && <> · Removed by {line.removedBy.join(', ')}</>}
            </p>

            <button
              type="button"
              onClick={() => setOpenProduct(openProduct === line.productId ? null : line.productId)}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-700"
            >
              <History className="h-3.5 w-3.5" aria-hidden />
              {openProduct === line.productId ? 'Hide history' : 'See how this was calculated'}
            </button>

            {openProduct === line.productId && (
              <ProductHistory
                productId={line.productId}
                businessId={businessId}
                currency={currency}
                timezone={timezone}
              />
            )}
          </div>
        ))}
      </div>

      {/* A real table where there is room for one. */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <th className="py-2 pr-3 font-medium">Product</th>
              <th className="py-2 pr-3 text-right font-medium">Received</th>
              <th className="py-2 pr-3 text-right font-medium">Sold</th>
              <th className="py-2 pr-3 text-right font-medium">Removed</th>
              <th className="py-2 pr-3 text-right font-medium">Expected</th>
              <th className="py-2 pr-3 text-right font-medium">Counted</th>
              <th className="py-2 pr-3 text-right font-medium">Difference</th>
              <th className="py-2 pr-3 text-right font-medium">Value</th>
              <th className="py-2 pr-3 font-medium">Removed by</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.productId} className="border-b border-slate-100 align-top">
                <td className="py-2 pr-3 font-medium text-slate-900">{line.productName}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{quantity(line.received)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{quantity(line.sold)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {quantity(line.wasted + Math.abs(line.adjusted))}
                </td>
                <td className="py-2 pr-3 text-right font-medium tabular-nums">
                  {quantity(line.onHand)}
                </td>
                {/*
                  Null means nobody has physically counted. Showing the expected
                  figure here would be the system checking its own work.
                */}
                <td className="py-2 pr-3 text-right tabular-nums text-slate-400">
                  {line.countedQuantity === null ? 'Not counted' : quantity(line.countedQuantity)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {line.difference === null ? '—' : quantity(line.difference)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {money(line.remainingValue, currency)}
                </td>
                <td className="py-2 pr-3 text-xs text-slate-500">
                  {line.removedBy.length > 0 ? line.removedBy.join(', ') : '—'}
                </td>
                <td className="py-2 text-right">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenProduct(openProduct === line.productId ? null : line.productId)
                    }
                    className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700"
                    aria-label={`History for ${line.productName}`}
                  >
                    History
                    <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {openProduct && (
          <div className="mt-3 rounded-xl border border-slate-200 p-3">
            <p className="mb-2 text-sm font-medium text-slate-900">
              {lines.find((l) => l.productId === openProduct)?.productName} — every movement
            </p>
            <ProductHistory
              productId={openProduct}
              businessId={businessId}
              currency={currency}
              timezone={timezone}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The movements behind one product's balance.
 *
 * The running balance comes from the API, not from adding up in the browser:
 * "how did we get to 18?" is an accounting question and belongs in one place.
 */
function ProductHistory({
  productId,
  businessId,
  currency,
  timezone,
}: {
  productId: string;
  businessId: string;
  currency: string;
  timezone: string;
}): ReactNode {
  const history = useQuery({
    queryKey: ['product-history', businessId, productId],
    queryFn: () =>
      api.get<{ data: ProductEvent[] }>(
        `/businesses/${businessId}/accountability/products/${productId}/history`,
      ),
  });

  if (history.isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner label="Loading history" />
      </div>
    );
  }

  if (history.isError) {
    return <p className="py-2 text-xs text-rose-700">Could not load this product's history.</p>;
  }

  const events = history.data?.data ?? [];
  if (events.length === 0) {
    return <p className="py-2 text-xs text-slate-500">No movements recorded.</p>;
  }

  return (
    <ol className="mt-2 space-y-2 border-l border-slate-200 pl-3">
      {events.map((event) => (
        <li key={event.id} className="text-xs">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <Badge tone={event.quantity < 0 ? 'danger' : 'neutral'}>{event.kind}</Badge>
            <span className="font-medium tabular-nums text-slate-900">
              {event.quantity > 0 ? '+' : ''}
              {quantity(event.quantity)}
            </span>
            <span className="text-slate-500">balance {quantity(event.runningBalance)}</span>
            {event.value !== null && (
              <span className="text-slate-500">{money(event.value, currency)}</span>
            )}
          </div>
          <p className="text-slate-500">
            {moment(event.occurredAt, timezone)}
            {event.staffName && <> · {event.staffName}</>}
            {event.note && <> · {event.note}</>}
          </p>
        </li>
      ))}
    </ol>
  );
}
